import { Modal, Tag } from '@carbon/react';
import { CHANGELOG, ChangeType } from '@/data/changelog';
import './ChangelogModal.scss';

// ── Tag colour per change type ─────────────────────────────────────────────

const TAG_TYPE: Record<
  ChangeType,
  'green' | 'blue' | 'purple' | 'red' | 'teal' | 'cyan' | 'gray' | 'warm-gray' | 'high-contrast' | 'outline' | 'cool-gray' | 'magenta'
> = {
  New: 'green',
  Improved: 'blue',
  Fixed: 'purple',
};

// ── Props ──────────────────────────────────────────────────────────────────

interface ChangelogModalProps {
  open: boolean;
  onClose: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────

export default function ChangelogModal({ open, onClose }: ChangelogModalProps) {
  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      passiveModal
      modalHeading="What's new"
      size="md"
      className="changelog-modal"
    >
      <div className="changelog-modal__body">
        {CHANGELOG.map((release) => {
          // Group entries by type in the order New → Improved → Fixed
          const groups: { label: string; type: ChangeType }[] = [
            { label: 'New', type: 'New' },
            { label: 'Improvements', type: 'Improved' },
            { label: 'Bug fixes', type: 'Fixed' },
          ];

          return (
            <section key={release.version} className="changelog-modal__release">
              {/* Release header */}
              <div className="changelog-modal__release-header">
                <span className="changelog-modal__release-version">
                  {release.version}
                </span>
                <span className="changelog-modal__release-date">
                  {release.date}
                </span>
              </div>

              {/* Optional summary */}
              {release.summary && (
                <p className="changelog-modal__release-summary">
                  {release.summary}
                </p>
              )}

              {/* Groups */}
              {groups.map(({ label, type }) => {
                const entries = release.changes.filter((c) => c.type === type);
                if (entries.length === 0) return null;

                return (
                  <div key={type} className="changelog-modal__group">
                    <h4 className="changelog-modal__group-heading">
                      <Tag type={TAG_TYPE[type]} size="sm">
                        {label}
                      </Tag>
                    </h4>
                    <ul className="changelog-modal__list">
                      {entries.map((entry) => (
                        <li key={entry.title} className="changelog-modal__entry">
                          <span className="changelog-modal__entry-title">
                            {entry.title}
                          </span>
                          <span className="changelog-modal__entry-description">
                            {entry.description}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>
    </Modal>
  );
}
